/**
 * An unfinished comment must survive the screen closing (Pavel 2026-08-07:
 * "kdyz se mu zavre obrazovka, nespadl mu Chrome, vypne si to nebo cokoliv,
 * tak aby neprisel o ta data. Aby ho to treba jako upozornilo, ze tam ma
 * nedokoncenou zpravu, jestli ji muze odeslat").
 *
 * Audio cannot live in localStorage, so the recorder's chunks go to IndexedDB
 * as they arrive (MediaRecorder emits one per second, so a crash costs at most
 * that second). A typed note goes to localStorage. On the next visit the
 * surface finds the leftover and OFFERS it; nothing is ever re-sent behind the
 * client's back.
 *
 * Every storage call is best effort. A blocked or full store must never break
 * a recording that is otherwise working, so all IO is wrapped and the failure
 * path is "no draft", not "no recording".
 */

export type VoiceDraftSurface = "deliverable" | "profile" | "beta";

export interface VoiceDraftRecord {
  id: string;
  surface: VoiceDraftSurface;
  targetId: string;
  mime: string;
  chunks: Blob[];
  /** `recording` died with the page; `unsent` finished but delivery failed. */
  state: "recording" | "unsent";
  transcript: string | null;
  startedAt: number;
  updatedAt: number;
}

const DB_NAME = "launchpad-voice";
const STORE = "drafts";
const DB_VERSION = 1;
/** A leftover older than this is noise, not a message the client still wants. */
export const VOICE_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Bound the store: a forgotten tab must not fill the client's disk. */
export const VOICE_DRAFT_MAX_BYTES = 60 * 1024 * 1024;

/** One draft per surface+target: a second recording replaces the abandoned one. */
export function voiceDraftId(surface: VoiceDraftSurface, targetId: string): string {
  return `${surface}:${targetId}`;
}

export function voiceDraftIsStale(record: { updatedAt: number }, now: number): boolean {
  return now - record.updatedAt > VOICE_DRAFT_MAX_AGE_MS;
}

/**
 * Is this leftover worth offering back? An abandoned take that never got past
 * the container header is noise; a finished-but-unsent note always counts.
 */
export function voiceDraftIsOfferable(
  record: { state: VoiceDraftRecord["state"]; chunks: { size: number }[]; updatedAt: number },
  now: number
): boolean {
  if (voiceDraftIsStale(record, now)) return false;
  if (record.state === "unsent") return true;
  return record.chunks.reduce((sum, chunk) => sum + chunk.size, 0) > 4_000;
}

export function voiceDraftBytes(record: { chunks: { size: number }[] }): number {
  return record.chunks.reduce((sum, chunk) => sum + chunk.size, 0);
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") {
        resolve(null);
        return;
      }
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "id" });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise<T | null>((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const request = run(tx.objectStore(STORE));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
      tx.oncomplete = () => db.close();
    } catch {
      resolve(null);
    }
  });
}

export async function readVoiceDraft(surface: VoiceDraftSurface, targetId: string): Promise<VoiceDraftRecord | null> {
  const record = await withStore<VoiceDraftRecord | undefined>("readonly", (store) => store.get(voiceDraftId(surface, targetId)));
  if (!record || !Array.isArray(record.chunks)) return null;
  if (!voiceDraftIsOfferable(record, Date.now())) {
    void dropVoiceDraft(surface, targetId);
    return null;
  }
  return record;
}

export async function writeVoiceDraft(record: VoiceDraftRecord): Promise<void> {
  if (voiceDraftBytes(record) > VOICE_DRAFT_MAX_BYTES) return;
  await withStore("readwrite", (store) => store.put(record));
}

export async function dropVoiceDraft(surface: VoiceDraftSurface, targetId: string): Promise<void> {
  await withStore("readwrite", (store) => store.delete(voiceDraftId(surface, targetId)));
}

/* ------------------------------------------------------------------ */
/* Typed drafts: same promise, cheaper storage.                        */
/* ------------------------------------------------------------------ */

const TEXT_PREFIX = "launchpad:note-draft:";

export function textDraftKey(surface: VoiceDraftSurface, targetId: string): string {
  return `${TEXT_PREFIX}${surface}:${targetId}`;
}

export function readTextDraft(surface: VoiceDraftSurface, targetId: string): string {
  try {
    const raw = window.localStorage.getItem(textDraftKey(surface, targetId));
    if (!raw) return "";
    const parsed = JSON.parse(raw) as { text?: unknown; updatedAt?: unknown };
    if (typeof parsed.text !== "string") return "";
    if (typeof parsed.updatedAt === "number" && voiceDraftIsStale({ updatedAt: parsed.updatedAt }, Date.now())) {
      dropTextDraft(surface, targetId);
      return "";
    }
    return parsed.text;
  } catch {
    return "";
  }
}

export function writeTextDraft(surface: VoiceDraftSurface, targetId: string, text: string): void {
  try {
    if (!text.trim()) {
      dropTextDraft(surface, targetId);
      return;
    }
    window.localStorage.setItem(textDraftKey(surface, targetId), JSON.stringify({ text, updatedAt: Date.now() }));
  } catch { /* storage full or blocked: the draft is a courtesy, not the note */ }
}

export function dropTextDraft(surface: VoiceDraftSurface, targetId: string): void {
  try {
    window.localStorage.removeItem(textDraftKey(surface, targetId));
  } catch { /* nothing to clean up */ }
}
